import React from "react";
import {
  Alert,
  Paper,
  ScrollArea,
  Skeleton,
  Stack,
  Table,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { IconAlertCircle, IconCalendarOff } from "@tabler/icons-react";
import Tooltip from "components/Tooltip/Tooltip";
import { useUser } from "stores/useUser";
import {
  canEditMeetingGoal,
  formatDay,
  formatDuration,
  formatTime,
  getMeetingCostLabel,
  getMeetingDuration,
  sortMeetingsByStart,
} from "./meetingDistUtils";
import AttendeeCount from "./AttendeeCount";
import MeetingDistGoalCell from "./MeetingDistGoalCell";

// Fixed widths keep the numeric columns on a single line — they were wrapping
// their own headers when the browser was left to size the table.
const COLUMNS = [
  { key: "title", label: "Title", width: 210 },
  { key: "when", label: "When", width: 104 },
  { key: "organizer", label: "Organizer", width: 190 },
  { key: "goal", label: "Strategic goal", width: null },
  { key: "attendees", label: "Attendees", width: 92, numeric: true },
  { key: "duration", label: "Duration", width: 88, numeric: true },
  { key: "cost", label: "Cost", width: 96, numeric: true },
];

const tableStyles = (theme) => ({
  tableLayout: "fixed",
  "&& thead tr th": {
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: theme.white,
    padding: "10px 12px",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    color: theme.colors.gray[6],
    whiteSpace: "nowrap",
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
  },
  "&& tbody tr td": {
    padding: "9px 12px",
    borderTop: `1px solid ${theme.colors.gray[1]}`,
    verticalAlign: "middle",
    overflow: "hidden",
  },
  "&& tbody tr:hover td": {
    backgroundColor: theme.colors.gray[0],
  },
  "&& tbody tr.clickable": {
    cursor: "pointer",
  },
  "&& tbody tr.clickable:focus-visible": {
    outline: `2px solid ${theme.colors.blue[4]}`,
    outlineOffset: "-2px",
  },
  "&& th.numeric, && td.numeric": {
    textAlign: "right",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
});

const TableShell = ({ children }) => (
  <Paper withBorder radius="md" sx={{ overflow: "hidden" }}>
    {children}
  </Paper>
);

// `goals`, `pendingGoals` and `onGoalChange` turn the goal column into a
// picker. Without `onGoalChange` the table is read-only, exactly as before.
const MeetingDistTable = ({
  meetingDetails,
  isLoading,
  isError,
  error,
  onSelectMeeting,
  goals,
  pendingGoals,
  onGoalChange,
}) => {
  const { user } = useUser();

  if (isLoading) {
    return (
      <TableShell>
        <Stack spacing={12} p="md">
          {[0, 1, 2, 3, 4].map((row) => (
            <Skeleton key={row} height={16} />
          ))}
        </Stack>
      </TableShell>
    );
  }

  if (isError) {
    return (
      <Alert
        icon={<IconAlertCircle size={16} />}
        title="Could not load meetings"
        color="red"
        variant="light"
        radius="md"
      >
        {error?.message || "Please try again."}
      </Alert>
    );
  }

  if (!meetingDetails || meetingDetails.length === 0) {
    return (
      <TableShell>
        <Stack align="center" spacing={6} py={32} px="md">
          <ThemeIcon size={40} radius="xl" variant="light" color="gray">
            <IconCalendarOff size={20} />
          </ThemeIcon>
          <Text size="sm" weight={600}>
            No meetings to show
          </Text>
          <Text size="xs" color="dimmed" align="center">
            Nothing matched this selection for the chosen period.
          </Text>
        </Stack>
      </TableShell>
    );
  }

  return (
    <TableShell>
      <ScrollArea.Autosize maxHeight={340} type="auto">
        <Table highlightOnHover sx={tableStyles}>
          <colgroup>
            {COLUMNS.map((column) => (
              <col
                key={column.key}
                style={column.width ? { width: column.width } : undefined}
              />
            ))}
          </colgroup>
          <thead>
            <tr>
              {COLUMNS.map((column) => (
                <th
                  key={column.key}
                  className={column.numeric ? "numeric" : undefined}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortMeetingsByStart(meetingDetails).map((meeting) => {
              const duration = getMeetingDuration(meeting);
              // One goal doc per recurring series, so a staged edit on one
              // occurrence shows on every sibling row.
              const docId = meeting.recurringEventId || meeting.id;
              const pending = pendingGoals?.[docId];
              const storedGoal = meeting.strategicGoal?.goal
                ? meeting.strategicGoal
                : { ...meeting.strategicGoal, goal: meeting.goal };
              const canEdit =
                Boolean(onGoalChange) && canEditMeetingGoal(meeting, user);

              return (
                <tr
                  key={meeting.id}
                  className={onSelectMeeting ? "clickable" : undefined}
                  role={onSelectMeeting ? "button" : undefined}
                  tabIndex={onSelectMeeting ? 0 : undefined}
                  onClick={
                    onSelectMeeting
                      ? () => onSelectMeeting(meeting)
                      : undefined
                  }
                  onKeyDown={
                    onSelectMeeting
                      ? (event) => {
                          if (event.key !== "Enter" && event.key !== " ") return;
                          event.preventDefault();
                          onSelectMeeting(meeting);
                        }
                      : undefined
                  }
                >
                  <td>
                    <Text
                      size="sm"
                      weight={500}
                      truncate
                      title={meeting.summary}
                    >
                      {meeting.summary || "No title"}
                    </Text>
                  </td>
                  <td>
                    <Text
                      size="xs"
                      weight={500}
                      sx={{ whiteSpace: "nowrap", lineHeight: 1.35 }}
                    >
                      {formatDay(meeting.start?.dateTime)}
                    </Text>
                    <Text
                      size="xs"
                      color="dimmed"
                      sx={{ whiteSpace: "nowrap", lineHeight: 1.35 }}
                    >
                      {formatTime(meeting.start?.dateTime)}
                    </Text>
                  </td>
                  <td>
                    <Text
                      size="xs"
                      color="dimmed"
                      truncate
                      title={meeting.organizer?.email}
                    >
                      {meeting.organizer?.email || "Unknown"}
                    </Text>
                  </td>
                  <td>
                    <MeetingDistGoalCell
                      goal={pending || storedGoal}
                      isPending={Boolean(pending)}
                      canEdit={canEdit}
                      showReadOnlyNote={Boolean(onGoalChange)}
                      goals={goals}
                      onSelect={(next) => onGoalChange(meeting, next)}
                    />
                  </td>
                  <td className="numeric">
                    <AttendeeCount attendees={meeting.attendees} />
                  </td>
                  <td className="numeric">
                    <Text size="sm" color="dimmed">
                      {formatDuration(duration)}
                    </Text>
                  </td>
                  <td className="numeric">
                    <Tooltip
                      withinPortal
                      styles={{ tooltip: { textAlign: "left" } }}
                      label={meeting?.costInfo?.toolTipMsg}
                      disabled={!meeting?.costInfo?.estimated}
                    >
                      <Text size="sm" weight={600}>
                        {getMeetingCostLabel(meeting?.costInfo)}
                      </Text>
                    </Tooltip>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </ScrollArea.Autosize>
    </TableShell>
  );
};

export default MeetingDistTable;
